import type { Role } from '@/api/auth';
import type { RestoreUserResult, UserStatus } from '@/api/users';

import { apiErrorCode } from './apiErrorCode';
import { humanizeError } from './errors';
import { normalizeForSearch } from './search';

/**
 * Решения меню «⋯» ученика в списке марафона и корзины — без разметки.
 *
 * Договор — `docs/CONTRACTS/users-stage4.md`, решения владельца F6 и 11.09.2026:
 * снимать и возвращать запись — admin и manager; блокировать, удалять и
 * восстанавливать — только admin.
 *
 * ⚠️ «ПЕРЕНЕСТИ НА ДРУГОЙ КУРС» В ЭТОМ МЕНЮ НЕТ И НЕ БУДЕТ. Перенос вместе с прогрессом
 * (этап 8, договор `enrollment-transfer.md` §9) живёт в карточке ученика: ему нужен
 * пробный прогон с таблицей чисел и журнал с откатом, строке меню это не по размеру.
 * До этапа 8 (решение владельца 11.09.2026) пункта не было вовсе — его нельзя было
 * собирать из «снять» и «записать»: такой перенос прогресса не переносит.
 */

export type RowAction =
  | 'profile'
  | 'copyAccess'
  | 'deactivate'
  | 'activate'
  | 'block'
  | 'unblock'
  | 'remove'
  | 'trash';

/**
 * Пункты меню строки.
 *
 * `account` — статус учётки. В строке записи его нет, меню подгружает его при
 * открытии; пока не пришёл — пунктов учётки нет, чтобы не предложить
 * «Заблокировать» уже заблокированному.
 */
export function rowActions(
  row: { status: string; student_email?: string },
  role: Role | undefined,
  account: UserStatus | undefined,
): RowAction[] {
  if (role !== 'admin' && role !== 'manager') return [];
  const isAdmin = role === 'admin';
  const out: RowAction[] = ['profile'];

  // Учётка в корзине: запись вернуть нельзя (сервер ответит `user_deleted`),
  // блокировать нечего, копировать доступы бессмысленно — вход закрыт. Только
  // восстановление в корзине.
  if (account === 'deleted') {
    if (isAdmin) out.push('trash');
    return out;
  }

  // «Скопировать данные для входа» — пунктом меню, а не кнопкой в строке
  // (решение владельца 11.09.2026): на телефоне кнопка давала строке третий ярус.
  // Ручка выданного пароля открыта admin и manager.
  if (row.student_email) out.push('copyAccess');

  // Истёкшую запись не снимают и не возвращают: сервер отвечает 409.
  if (row.status === 'active') out.push('deactivate');
  else if (row.status === 'inactive') out.push('activate');

  if (isAdmin && account) {
    out.push(account === 'blocked' ? 'unblock' : 'block');
    out.push('remove');
  }
  return out;
}

/** Код отказа сервера → ключ перевода. Различать причины только по `code`, не по тексту. */
const CODE_KEYS: Record<string, string> = {
  student_in_group: 'userLifecycle.errStudentInGroup',
  enrollment_inactive: 'userLifecycle.errEnrollmentInactive',
  user_deleted: 'userLifecycle.errUserDeleted',
  active_enrollment_exists: 'userLifecycle.errActiveExists',
  primary_enrollment_exists: 'userLifecycle.errPrimaryExists',
  enrollment_not_active: 'userLifecycle.errNotActive',
  enrollment_not_inactive: 'userLifecycle.errNotInactive',
  email_taken: 'userLifecycle.errEmailTaken',
  user_not_deleted: 'userLifecycle.errNotDeleted',
  cannot_remove_self: 'userLifecycle.errRemoveSelf',
  user_not_found: 'userLifecycle.errUserNotFound',
  enrollment_not_found: 'userLifecycle.errEnrollmentNotFound',
  // «Убрать из группы»: запись уже не в этой группе (users-service, техдолг 183).
  not_in_group: 'userLifecycle.errNotInGroup',
};

/**
 * Ключ текста отказа или `null`, если сказать нечего сверх общего текста.
 *
 * ⚠️ Без этого каждый 409 превращался бы в `humanizeError` — «такая запись уже
 * существует», что для «ученик в группе» или «учётка в корзине» просто неправда.
 * 403 приходит от общей охраны ролей без `code`.
 */
export function lifecycleErrorKey(err: unknown): string | null {
  const code = apiErrorCode(err);
  if (code) return CODE_KEYS[code] ?? null;
  const status = (err as { status?: unknown } | null | undefined)?.status;
  return status === 403 ? 'userLifecycle.errForbidden' : null;
}

/** Текст отказа: свой по коду, иначе общий. */
export function lifecycleErrorText(err: unknown, t: (key: string) => string): string {
  const key = lifecycleErrorKey(err);
  return key ? t(key) : humanizeError(err);
}

/**
 * Срок записи по умолчанию (решение владельца 11.10.2026): основной марафон — 90 дней от начала доступа,
 * курс «один на один» (`access_category = one_to_one`) — 365. Тот же расчёт делает сервер (users-service
 * `defaultDays`), когда срок не прислан; здесь — чтобы показать число в поле до отправки.
 */
export const DEFAULT_ACCESS_DAYS = 90;
export const ONE_TO_ONE_ACCESS_DAYS = 365;

export function accessDefaultDays(category: string | null | undefined): number {
  return category === 'one_to_one' ? ONE_TO_ONE_ACCESS_DAYS : DEFAULT_ACCESS_DAYS;
}

const DAY_MS = 86_400_000;

/**
 * Сколько дней подставить в «Продлить»: до `access_from` + 90 (365 «один на один»).
 *
 * Сервер прибавляет дни к прежнему концу (`access_until`), поэтому считаем от него. 0 — запись уже доходит до
 * этого конца или дальше: поле остаётся пустым, кнопка серая, пока админ не впишет число сам. До 11.10.2026 здесь
 * стояло 30 всегда — и 10.09 такими числами вписали ученикам 30 дней вместо 90.
 */
export function defaultExtendDays(
  row: { access_from: string; access_until: string } | undefined,
  category: string | null | undefined,
): number {
  if (!row) return 0;
  const from = Date.parse(row.access_from);
  const until = Date.parse(row.access_until);
  if (Number.isNaN(from) || Number.isNaN(until)) return 0;
  const target = from + accessDefaultDays(category) * DAY_MS;
  return Math.max(0, Math.ceil((target - until) / DAY_MS));
}

/** Доп. курс, не пошедший за основным при продлении, — курс и ключ причины по `code` сервера. */
export function extrasSkippedReasons(
  skipped: readonly { marathon_id: string; code: string }[] | null | undefined,
): { marathonId: string; key: string }[] {
  return (skipped ?? []).map((s) => ({
    marathonId: s.marathon_id,
    key: CODE_KEYS[s.code] ?? 'adminDetail.extraSkippedOther',
  }));
}

/**
 * Какая запись выбрана в блоке «Доступ к курсам» карточки ученика — одна на выпадашку, тариф и «Продлить».
 *
 * ⚠️ Выбор, которого нет в списке (запись сняли, ученика сменили), и пустой выбор — первая запись. Раньше
 * «Продлить» брал сырой выбор: при одной записи выпадашки нет, выбор пуст, и уходил `POST /admin/enrollments//extend`;
 * при двух — первую запись нельзя было продлить, не переключившись на вторую и обратно (разбор удобства 28.09, №1).
 */
export function pickedEnrollmentId(picked: string, list: readonly { id: string }[]): string {
  return list.some((e) => e.id === picked) ? picked : (list[0]?.id ?? '');
}

/**
 * D25: «назначить куратора» — только активной записи без группы.
 *
 * У ученика в группе кураторы — это кураторы группы, сервер отвечает 409
 * `student_in_group`. Снятой записи назначать некого.
 */
export function canAssignCurator(row: { status: string; group_id?: string | null }): boolean {
  return row.status === 'active' && !row.group_id;
}

/**
 * Кнопка «Перенести на другой курс» в карточке ученика (28.09.2026).
 *
 * Ручки переноса — только admin (`enrollment_transfer_handler.go`): менеджеру кнопка
 * пообещала бы то, что сервер отклонит 403. Учётка в корзине — сервер ответит
 * `student_not_found`; заблокированная — переносится (блокировка закрывает вход, а не
 * данные).
 */
export function canTransfer(role: Role | undefined, account: UserStatus | undefined): boolean {
  return role === 'admin' && !!account && account !== 'deleted';
}

/** Итог восстановления числами — для текста админу. */
export function restoreSummary(res: RestoreUserResult): { restored: number; skipped: number; primaryDropped: number } {
  return {
    restored: res.restored_enrollments ?? 0,
    skipped: res.skipped?.length ?? 0,
    primaryDropped: res.primary_dropped?.length ?? 0,
  };
}

/**
 * Поиск по списку учеников марафона — по имени и почте, на клиенте.
 *
 * ⚠️ БЕЗ ЗАПРОСА НА СЕРВЕР. Вкладка получает всех учеников марафона одним ответом
 * (`GET /admin/enrollments?marathon_id=…`), фильтр идёт по нему — решение
 * владельца 11.09.2026, новых ручек не заводим. «ё» и «е» не различаем: в именах
 * их пишут как попало.
 */
export function filterEnrollmentRows<T extends { student_name?: string; student_email?: string }>(
  rows: readonly T[],
  query: string,
): T[] {
  const q = normalizeForSearch(query);
  if (!q) return [...rows];
  return rows.filter(
    (r) => normalizeForSearch(r.student_name).includes(q) || normalizeForSearch(r.student_email).includes(q),
  );
}

const STATUS_KEYS: Record<string, string> = {
  active: 'userLifecycle.enrActive',
  inactive: 'userLifecycle.enrInactive',
  expired: 'userLifecycle.enrExpired',
  pending: 'userLifecycle.enrPending',
  cancelled: 'userLifecycle.enrCancelled',
};

/** Статус записи словом. До этапа 4 в списке стояло сырое `active` / `inactive`. */
export function enrollmentStatusKey(status: string): string | null {
  return STATUS_KEYS[status] ?? null;
}
